import { useState, useMemo, useCallback } from 'react';
import { Table2, Key, Link2, Copy, Check, Code2 } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { ActionPanel } from '@/components/shared/ActionPanel';
import { buildTableActions } from '@/lib/actions/table-actions';
import { getTableActionDeps } from '@/lib/actions/table-deps';
import type { StructureViewTab, ColumnMeta, TableMeta } from '@/types';

interface TableStructureResponse extends TableMeta {
  ddl: string;
}

interface StructureViewProps {
  tab: StructureViewTab;
}

// ─── DDL Generation ─────────────────────────────────────────

function generateDDL(schema: string, table: string, columns: ColumnMeta[]): string {
  const lines = columns.map((col) => {
    let line = `  "${col.name}" ${col.type}`;
    if (!col.nullable) line += ' NOT NULL';
    if (col.defaultValue) line += ` DEFAULT ${col.defaultValue}`;
    return line;
  });

  const pks = columns.filter((c) => c.isPrimaryKey).map((c) => `"${c.name}"`);
  if (pks.length > 0) {
    lines.push(`  PRIMARY KEY (${pks.join(', ')})`);
  }

  const fks = columns.filter((c) => c.isForeignKey && c.referencesTable);
  for (const fk of fks) {
    lines.push(
      `  FOREIGN KEY ("${fk.name}") REFERENCES "${fk.referencesTable}" ("${fk.referencesColumn ?? fk.name}")`
    );
  }

  return `CREATE TABLE "${schema}"."${table}" (\n${lines.join(',\n')}\n);`;
}

// ─── TypeScript Interface Generation ────────────────────────

const SQL_TO_TS: Record<string, string> = {
  'integer': 'number',
  'int': 'number',
  'int2': 'number',
  'int4': 'number',
  'int8': 'number',
  'smallint': 'number',
  'bigint': 'number',
  'serial': 'number',
  'bigserial': 'number',
  'smallserial': 'number',
  'float': 'number',
  'float4': 'number',
  'float8': 'number',
  'real': 'number',
  'double precision': 'number',
  'decimal': 'number',
  'numeric': 'number',
  'money': 'number',
  'boolean': 'boolean',
  'bool': 'boolean',
  'text': 'string',
  'varchar': 'string',
  'character varying': 'string',
  'char': 'string',
  'character': 'string',
  'uuid': 'string',
  'citext': 'string',
  'name': 'string',
  'date': 'string',
  'timestamp': 'string',
  'timestamp without time zone': 'string',
  'timestamp with time zone': 'string',
  'timestamptz': 'string',
  'time': 'string',
  'time without time zone': 'string',
  'time with time zone': 'string',
  'timetz': 'string',
  'interval': 'string',
  'json': 'unknown',
  'jsonb': 'unknown',
  'bytea': 'Buffer',
  'inet': 'string',
  'cidr': 'string',
  'macaddr': 'string',
  'xml': 'string',
  'point': 'string',
  'line': 'string',
  'polygon': 'string',
  'tsvector': 'string',
  'tsquery': 'string',
};

function mapSqlTypeToTs(sqlType: string): string {
  const lower = sqlType.toLowerCase().trim();
  // Direct match
  if (SQL_TO_TS[lower]) return SQL_TO_TS[lower];
  // Check if type starts with a known prefix (e.g. "varchar(255)" -> "varchar")
  const base = lower.replace(/\(.*\)/, '').trim();
  if (SQL_TO_TS[base]) return SQL_TO_TS[base];
  // Array types
  if (lower.endsWith('[]')) return `${mapSqlTypeToTs(lower.slice(0, -2))}[]`;
  return 'string';
}

function generateTypeScript(table: string, columns: ColumnMeta[]): string {
  const lines = columns.map((col) => {
    const tsType = mapSqlTypeToTs(col.type);
    const optional = col.nullable ? '?' : '';
    return `  ${col.name}${optional}: ${tsType};`;
  });

  // PascalCase the table name
  const name = table
    .split(/[_\-\s]+/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join('');

  return `interface ${name} {\n${lines.join('\n')}\n}`;
}

// ─── Copy Button with Feedback ──────────────────────────────

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-text-secondary bg-dbgo-panel border rounded-md hover:text-text-primary hover:border-strong transition-colors select-none"
    >
      {copied ? (
        <>
          <Check size={13} className="text-accent-green" />
          <span className="text-accent-green">Copied!</span>
        </>
      ) : (
        <>
          <Copy size={13} />
          {label}
        </>
      )}
    </button>
  );
}

// ─── Main Component ─────────────────────────────────────────

export function StructureView({ tab }: StructureViewProps) {
  const openTab = useWorkspaceStore((s) => s.openTab);
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery<TableStructureResponse>({
    queryKey: ['table-structure', tab.connectionId, tab.database, tab.schema, tab.table],
    queryFn: () => {
      const qs = tab.database ? `?database=${encodeURIComponent(tab.database)}` : '';
      return apiClient.get<TableStructureResponse>(
        `/api/schema/${tab.connectionId}/tables/${encodeURIComponent(tab.schema)}/${encodeURIComponent(tab.table)}${qs}`,
      );
    },
    staleTime: 60_000,
  });

  const ddl = useMemo(() => {
    if (!data?.columns) return '';
    return generateDDL(tab.schema, tab.table, data.columns);
  }, [data?.columns, tab.schema, tab.table]);

  const tsInterface = useMemo(() => {
    if (!data?.columns) return '';
    return generateTypeScript(tab.table, data.columns);
  }, [data?.columns, tab.table]);

  const primaryKeys = useMemo(
    () => data?.columns.filter((c) => c.isPrimaryKey) ?? [],
    [data?.columns],
  );

  const foreignKeys = useMemo(
    () => data?.columns.filter((c) => c.isForeignKey) ?? [],
    [data?.columns],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-text-muted">
        Loading structure...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-accent-red">
        {error instanceof Error ? error.message : 'Failed to load table structure'}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="h-full overflow-y-auto bg-dbgo-workarea">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-dbgo-panel border flex items-center justify-center">
              <Table2 size={20} className="text-accent-blue" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-text-primary">
                <span className="text-text-secondary">{tab.schema}.</span>
                {tab.table}
              </h2>
              <p className="text-xs text-text-secondary">
                {data.columns.length} column{data.columns.length !== 1 ? 's' : ''}
                {data.rowCountEstimate != null && (
                  <span className="ml-2">
                    ~{data.rowCountEstimate.toLocaleString()} rows
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* "Copy CREATE TABLE" and "Copy as TypeScript" are unique to the
                Structure view (generated from this view's column metadata).
                Every other action lives in the Actions panel below. */}
            <CopyButton text={ddl} label="Copy CREATE TABLE" />
            <CopyButton text={tsInterface} label="Copy as TypeScript" />
          </div>
        </div>

        {/* Actions — mirrors the sidebar's right-click menu for this table,
            rendered as iOS Settings-style grouped cards for touch use. */}
        <ActionPanel
          items={buildTableActions(
            tab.schema,
            tab.table,
            getTableActionDeps({
              connectionId: tab.connectionId,
              schema: tab.schema,
              table: tab.table,
              openTab,
              queryClient,
              tableColumns: data.columns.map((c) => ({ name: c.name, type: c.type })),
            }),
            data.rowCountEstimate,
          )}
        />

        {/* Columns table */}
        <div className="mb-6">
          <h3 className="text-2xs font-semibold text-text-secondary uppercase tracking-widest mb-3">
            Columns
          </h3>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-dbgo-panel text-text-secondary">
                  <th className="text-left px-3 py-2 font-medium w-8">#</th>
                  <th className="text-left px-3 py-2 font-medium">Name</th>
                  <th className="text-left px-3 py-2 font-medium">Type</th>
                  <th className="text-center px-3 py-2 font-medium">Nullable</th>
                  <th className="text-left px-3 py-2 font-medium">Default</th>
                  <th className="text-center px-3 py-2 font-medium">PK</th>
                  <th className="text-left px-3 py-2 font-medium">FK Reference</th>
                </tr>
              </thead>
              <tbody>
                {data.columns.map((col, idx) => (
                  <tr
                    key={col.name}
                    className={`border-t border-subtle transition-colors hover:bg-dbgo-grid-row-hover ${
                      idx % 2 === 0 ? 'bg-dbgo-workarea' : 'bg-dbgo-grid-row-alt'
                    }`}
                  >
                    <td className="px-3 py-1.5 text-text-muted font-mono">{idx + 1}</td>
                    <td className="px-3 py-1.5 font-mono text-text-primary font-medium">
                      {col.name}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-syn-type">{col.type}</td>
                    <td className="px-3 py-1.5 text-center">
                      {col.nullable ? (
                        <span className="text-text-muted" title="Nullable">--</span>
                      ) : (
                        <span className="text-accent-red font-semibold" title="NOT NULL">
                          NOT NULL
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-text-secondary">
                      {col.defaultValue ?? <span className="text-text-muted">--</span>}
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      {col.isPrimaryKey && (
                        <span title="Primary Key">
                          <Key size={14} className="inline text-accent-yellow" />
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      {col.isForeignKey && col.referencesTable ? (
                        <span className="flex items-center gap-1 text-accent-blue">
                          <Link2 size={13} className="flex-shrink-0" />
                          <span className="font-mono">
                            {col.referencesTable}
                            {col.referencesColumn && `.${col.referencesColumn}`}
                          </span>
                        </span>
                      ) : (
                        <span className="text-text-muted">--</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Primary Keys section */}
        {primaryKeys.length > 0 && (
          <div className="mb-6">
            <h3 className="text-2xs font-semibold text-text-secondary uppercase tracking-widest mb-3">
              Primary Key
            </h3>
            <div className="border rounded-lg overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 bg-dbgo-panel">
                <Key size={14} className="text-accent-yellow" />
                <span className="text-xs font-mono text-text-primary">
                  ({primaryKeys.map((c) => c.name).join(', ')})
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Foreign Keys section */}
        {foreignKeys.length > 0 && (
          <div className="mb-6">
            <h3 className="text-2xs font-semibold text-text-secondary uppercase tracking-widest mb-3">
              Foreign Keys
            </h3>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-dbgo-panel text-text-secondary">
                    <th className="text-left px-3 py-2 font-medium">Column</th>
                    <th className="text-left px-3 py-2 font-medium">References</th>
                  </tr>
                </thead>
                <tbody>
                  {foreignKeys.map((fk) => (
                    <tr
                      key={fk.name}
                      className="border-t border-subtle hover:bg-dbgo-grid-row-hover bg-dbgo-workarea"
                    >
                      <td className="px-3 py-1.5 font-mono text-text-primary">{fk.name}</td>
                      <td className="px-3 py-1.5">
                        <span className="flex items-center gap-1 text-accent-blue">
                          <Link2 size={13} className="flex-shrink-0" />
                          <span className="font-mono">
                            {fk.referencesTable}
                            {fk.referencesColumn && `.${fk.referencesColumn}`}
                          </span>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* DDL Preview */}
        <div className="mb-6">
          <h3 className="text-2xs font-semibold text-text-secondary uppercase tracking-widest mb-3">
            DDL
          </h3>
          <div className="border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 bg-dbgo-panel border-b">
              <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                <Code2 size={13} />
                CREATE TABLE
              </div>
              <CopyButton text={ddl} label="Copy" />
            </div>
            <pre className="px-4 py-3 text-xs font-mono text-text-primary bg-dbgo-editor overflow-x-auto whitespace-pre">
              {ddl}
            </pre>
          </div>
        </div>

        {/* TypeScript Preview */}
        <div className="mb-6">
          <h3 className="text-2xs font-semibold text-text-secondary uppercase tracking-widest mb-3">
            TypeScript Interface
          </h3>
          <div className="border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 bg-dbgo-panel border-b">
              <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                <Code2 size={13} />
                Interface
              </div>
              <CopyButton text={tsInterface} label="Copy" />
            </div>
            <pre className="px-4 py-3 text-xs font-mono text-text-primary bg-dbgo-editor overflow-x-auto whitespace-pre">
              {tsInterface}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
